import { useMyAccountQuery } from '@/features/users/hooks/use-users-query'
import { FEATURES } from '@/shared/config/features'
import { useStaffProfileQuery } from './use-staff-profile-query'

/** Identidad mínima que el sidebar necesita para pintar al usuario en sesión. */
export type CurrentAccountIdentity = {
  fullName: string | undefined
  email: string | undefined
  avatar: string | null
  /** Semilla del avatar generado. Ver la nota sobre estabilidad más abajo. */
  seed: string | undefined
}

/**
 * Identidad de la cuenta en sesión, ramificada por `webAuthV2`: con el flag prendido sale
 * de `GET /api/v2/me/profile`; apagado, del `me` v1. Los dos hooks se llaman siempre (nada
 * de hooks condicionales) pero sólo uno queda habilitado, así que sale una sola request.
 *
 * La rama v1 muere junto con el flag; cuando sea permanente, este hook se colapsa a
 * `useStaffProfileQuery`.
 *
 * La semilla del avatar generado es el **correo**, no el id: es el único identificador que
 * existe en los dos contratos (v1 expone `_id` de Mongo, v2 sólo el `usr_` público), así que
 * usarlo evita que el avatar de quien no cargó foto cambie al prender o revertir el flag.
 */
export function useCurrentAccountIdentity(): CurrentAccountIdentity {
  const staffProfile = useStaffProfileQuery()
  const legacyAccount = useMyAccountQuery(!FEATURES.webAuthV2)

  if (FEATURES.webAuthV2) {
    const profile = staffProfile.data
    return {
      fullName: profile?.fullName,
      email: profile?.email,
      avatar: profile?.avatar ?? null,
      seed: profile?.email
    }
  }

  const account = legacyAccount.data
  return {
    fullName: account?.fullname,
    email: account?.email,
    avatar: account?.avatar ?? null,
    seed: account?.email
  }
}
